import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "@/components/Avatar";
import Seo from "@/components/Seo";
import { toast } from "sonner";
import { ArrowLeft, Users, Send, Trash2, BookOpen, Calendar, ExternalLink, Palette, Pencil, Check, X } from "lucide-react";
import { getThemesForScope, getPostTheme, THEMED_MAX_CHARS } from "@/lib/postThemes";
import { ShareCardDialog } from "@/components/ShareCardDialog";

const BOOKCLUB_THEMES = getThemesForScope("bookclub");

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ClubPost({ p, user, clubId, onChange, onDelete, onShare }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(p.content);
  const [draftBg, setDraftBg] = useState(p.background || "none");
  const [bgOpen, setBgOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const canEdit = user && (user.role === "admin" || p.author_id === user.id);
  const canDelete = user && (user.role === "admin" || p.author_id === user.id);
  const themeId = p.background && p.background !== "none" ? p.background : null;
  const showThemedHero = !!themeId && !editing && (p.content?.length || 0) <= THEMED_MAX_CHARS;
  const theme = getPostTheme(themeId);

  const startEdit = () => { setDraft(p.content); setDraftBg(p.background || "none"); setEditing(true); };
  const cancelEdit = () => { setEditing(false); setBgOpen(false); };
  const saveEdit = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/book-clubs/${clubId}/posts/${p.id}`, { content: draft, background: draftBg });
      onChange(data);
      setEditing(false); setBgOpen(false);
      toast.success("Post updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <li className="jj-card p-4 flex gap-3" data-testid={`club-post-${p.id}`}>
        <Avatar user={{ id: p.author_id, name: p.author_name, avatar_path: p.author_avatar }} size={36} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-jjblue">{p.author_name || "Member"}</div>
          <textarea
            rows={3}
            maxLength={4000}
            className="input-jj mt-2 !rounded-xl"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid={`club-post-edit-input-${p.id}`}
          />
          <div className="mt-2 flex items-center gap-2">
            <button type="button" onClick={() => setBgOpen((v) => !v)}
              className="btn-secondary !py-1 !px-2 text-[11px] flex items-center gap-1"
              data-testid={`club-post-edit-theme-toggle-${p.id}`}>
              <Palette className="h-3 w-3" strokeWidth={1.6} /> Background {draftBg !== "none" ? `· ${draftBg}` : ""}
            </button>
          </div>
          {bgOpen && (
            <div className="mt-2 flex flex-wrap gap-2" data-testid={`club-post-edit-theme-picker-${p.id}`}>
              {BOOKCLUB_THEMES.map((t) => (
                <button key={t.id} type="button" onClick={() => setDraftBg(t.id)}
                  title={t.label}
                  className={`h-7 w-7 rounded-full border-2 transition ${draftBg === t.id ? "border-[var(--jj-blue)] scale-110" : "border-white hover:scale-105"} shadow-sm`}
                  style={{ background: t.swatch }}
                  data-testid={`club-post-edit-theme-${t.id}-${p.id}`}
                />
              ))}
            </div>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={cancelEdit} className="btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`club-post-edit-cancel-${p.id}`}>
              <X className="h-3.5 w-3.5" /> Cancel
            </button>
            <button disabled={saving} onClick={saveEdit} className="btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5" data-testid={`club-post-edit-save-${p.id}`}>
              <Check className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </li>
    );
  }

  if (showThemedHero) {
    return (
      <li
        className="rounded-2xl p-5 flex gap-3 relative overflow-hidden"
        style={{ background: theme.background, color: theme.text }}
        data-testid={`club-post-${p.id}`}
      >
        <Avatar user={{ id: p.author_id, name: p.author_name, avatar_path: p.author_avatar }} size={36} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium" style={{ color: theme.text }}>
              <Link to={`/u/${p.author_id}`} className="hover:underline" style={{ color: theme.text }}>{p.author_name || "Member"}</Link>
            </div>
            <div className="flex items-center gap-2 opacity-80">
              <span className="text-[11px]" style={{ color: theme.text }}>{timeAgo(p.created_at)}</span>
              <button onClick={() => onShare(p.id)} className="p-1 opacity-80 hover:opacity-100"
                title="Send as image" data-testid={`club-post-send-image-${p.id}`} style={{ color: theme.text }}>
                <Send className="h-3 w-3" />
              </button>
              {canEdit && (
                <button onClick={startEdit} className="p-1 opacity-80 hover:opacity-100" title="Edit" data-testid={`club-post-edit-${p.id}`} style={{ color: theme.text }}>
                  <Pencil className="h-3 w-3" />
                </button>
              )}
              {canDelete && (
                <button onClick={() => onDelete(p)} className="p-1 opacity-80 hover:opacity-100" title="Delete" data-testid={`club-post-delete-${p.id}`} style={{ color: theme.text }}>
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
          <p className="mt-2 font-serif text-lg sm:text-xl leading-snug whitespace-pre-wrap break-words">{p.content}</p>
        </div>
      </li>
    );
  }

  return (
    <li className="jj-card p-4 flex gap-3" data-testid={`club-post-${p.id}`}>
      <Avatar user={{ id: p.author_id, name: p.author_name, avatar_path: p.author_avatar }} size={36} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-sm font-medium text-jjblue">
            <Link to={`/u/${p.author_id}`} className="hover:text-burgundy hover:underline">{p.author_name || "Member"}</Link>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-[var(--jj-muted)]">{timeAgo(p.created_at)}</span>
            {canEdit && (
              <button onClick={startEdit} className="p-1 text-[var(--jj-muted)] hover:text-burgundy" title="Edit" data-testid={`club-post-edit-${p.id}`}>
                <Pencil className="h-3 w-3" />
              </button>
            )}
            {canDelete && (
              <button onClick={() => onDelete(p)} className="p-1 text-[var(--jj-muted)] hover:text-burgundy" title="Delete" data-testid={`club-post-delete-${p.id}`}>
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
        <p className="mt-1 text-sm text-jjblue/90 whitespace-pre-wrap break-words">{p.content}</p>
      </div>
    </li>
  );
}

function EditClubDialog({ open, club, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [schedule, setSchedule] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && club) {
      setName(club.name || "");
      setDescription(club.description || "");
      setSchedule(club.meeting_schedule || "");
    }
  }, [open, club]);

  if (!open || !club) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the club a name."); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/book-clubs/${club.id}`, {
        name, description, meeting_schedule: schedule,
      });
      onSaved(data);
      toast.success("Club updated");
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-jjblue/60 backdrop-blur-sm" data-testid="club-edit-dialog">
      <div className="jj-card w-full max-w-lg p-7 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--jj-muted)] hover:text-burgundy"><X className="h-5 w-5" /></button>
        <div className="overline text-burgundy">Edit club</div>
        <h2 className="font-serif text-2xl text-jjblue mt-2 leading-tight">Update the room</h2>
        <form onSubmit={submit} className="mt-5 space-y-3">
          <input className="input-jj" placeholder="Club name" value={name} onChange={(e) => setName(e.target.value)} data-testid="club-edit-name" />
          <input className="input-jj" placeholder="Meeting schedule (e.g. Mondays 7pm CT)" value={schedule} onChange={(e) => setSchedule(e.target.value)} data-testid="club-edit-schedule" />
          <textarea rows={4} maxLength={2000} className="input-jj" placeholder="What is this club about?" value={description} onChange={(e) => setDescription(e.target.value)} data-testid="club-edit-desc" />
          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary !py-2 !px-4 text-sm">Cancel</button>
            <button disabled={busy} className="btn-primary !py-2 !px-5 text-sm" data-testid="club-edit-submit">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function BookClubDetail() {
  const { clubId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [background, setBackground] = useState("none");
  const [themeOpen, setThemeOpen] = useState(false);
  const [sharePostId, setSharePostId] = useState(null);
  const [editClubOpen, setEditClubOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, p] = await Promise.all([
        api.get(`/book-clubs/${clubId}`),
        api.get(`/book-clubs/${clubId}/posts`),
      ]);
      setClub(c.data);
      setPosts(p.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Club not found");
      navigate("/book-club");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clubId]);

  const join = async () => {
    try { const { data } = await api.post(`/book-clubs/${clubId}/join`); setClub(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not join"); }
  };
  const leave = async () => {
    if (!window.confirm("Leave this book club?")) return;
    try { const { data } = await api.post(`/book-clubs/${clubId}/leave`); setClub(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not leave"); }
  };

  const deleteClub = async () => {
    if (!window.confirm(`Delete "${club.name}" and its discussion?`)) return;
    try {
      await api.delete(`/book-clubs/${clubId}`);
      toast.success("Club removed");
      navigate("/book-club");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const submitPost = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/book-clubs/${clubId}/posts`, { content, background });
      setPosts((arr) => [...arr, data]);
      setContent(""); setBackground("none"); setThemeOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };

  const deletePost = async (p) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/book-clubs/${clubId}/posts/${p.id}`);
      setPosts((arr) => arr.filter((x) => x.id !== p.id));
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  if (loading || !club) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-[var(--jj-muted)]">Loading…</div>;
  }

  const canDeleteClub = user && (user.role === "admin" || club.host_id === user.id);
  const canEditClub = user && (user.role === "admin" || club.host_id === user.id);
  const canPost = user && (club.i_joined || user.role === "admin");

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Seo title={`${club.name} · Book Club · Jesus Just Us`} description={club.description} path={`/book-club/clubs/${clubId}`} />
      <Link to="/book-club" className="inline-flex items-center gap-1 text-sm text-burgundy hover:underline mb-6" data-testid="club-back">
        <ArrowLeft className="h-4 w-4" /> Back to Book Club
      </Link>

      <div className="jj-card p-6 sm:p-7 flex gap-5 items-start flex-wrap" data-testid="club-header">
        {club.book_cover_url && (
          <div className="aspect-[2/3] w-24 rounded-lg overflow-hidden shrink-0 bg-cream-surface">
            <img src={club.book_cover_url} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-[220px]">
          <div className="overline text-burgundy flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Book club</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-jjblue mt-2 leading-tight">{club.name}</h1>
          {club.book_title && <div className="mt-2 text-sm text-jjblue/85 inline-flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5 text-burgundy" /> Reading <span className="font-medium">{club.book_title}</span></div>}
          {club.meeting_schedule && <div className="mt-1 text-xs text-[var(--jj-muted)] inline-flex items-center gap-1.5"><Calendar className="h-3 w-3" /> {club.meeting_schedule}</div>}
          {club.description && <p className="mt-3 text-sm text-[var(--jj-text-secondary)] leading-relaxed whitespace-pre-wrap">{club.description}</p>}
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-[var(--jj-muted)]"><Users className="inline h-3 w-3 mr-1" />{club.member_count} member{club.member_count === 1 ? "" : "s"}</span>
            <span className="text-xs text-[var(--jj-muted)]">· hosted by <Link to={`/u/${club.host_id}`} className="text-jjblue hover:text-burgundy hover:underline">{club.host_name || "a member"}</Link></span>
            <span className="ml-auto inline-flex items-center gap-2">
              {user ? (
                club.i_joined ? (
                  <button onClick={leave} className="btn-secondary !py-1.5 !px-3 text-xs" data-testid="club-detail-leave">Leave</button>
                ) : (
                  <button onClick={join} className="btn-primary !py-1.5 !px-3 text-xs" data-testid="club-detail-join">Join club</button>
                )
              ) : (
                <Link to="/login" className="text-xs text-burgundy hover:underline">Sign in to join</Link>
              )}
              {canEditClub && (
                <button onClick={() => setEditClubOpen(true)} className="p-1.5 text-[var(--jj-muted)] hover:text-burgundy" title="Edit club" data-testid="club-detail-edit">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
              {canDeleteClub && (
                <button onClick={deleteClub} className="p-1.5 text-[var(--jj-muted)] hover:text-burgundy" title="Delete club" data-testid="club-detail-delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          </div>
        </div>
      </div>

      <section className="mt-8">
        <div className="overline text-burgundy">Discussion</div>
        <h2 className="font-serif text-2xl text-jjblue mt-1 leading-tight">Talk it through together.</h2>

        {posts.length === 0 ? (
          <p className="mt-6 text-sm text-[var(--jj-muted)]">No messages yet — be the first to share a thought.</p>
        ) : (
          <ul className="mt-6 space-y-4" data-testid="club-posts">
            {posts.map((p) => (
              <ClubPost
                key={p.id}
                p={p}
                user={user}
                clubId={clubId}
                onChange={(updated) => setPosts((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)))}
                onDelete={deletePost}
                onShare={setSharePostId}
              />
            ))}
          </ul>
        )}

        {canPost ? (
          (() => {
            const activeTheme = getPostTheme(background);
            const themedPreview = background !== "none" && content.length > 0 && content.length <= THEMED_MAX_CHARS;
            return (
              <form onSubmit={submitPost} className="mt-6 jj-card p-4" data-testid="club-composer">
                {themedPreview ? (
                  <div
                    className="rounded-2xl px-5 py-6 transition-colors duration-300"
                    style={{ background: activeTheme.background, color: activeTheme.text }}
                    data-testid="club-themed-preview"
                  >
                    <textarea
                      rows={3}
                      maxLength={THEMED_MAX_CHARS}
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Share a thought, a favorite passage, a question…"
                      className="w-full bg-transparent border-none focus:outline-none resize-none font-serif text-lg sm:text-xl leading-snug placeholder:opacity-60"
                      style={{ color: activeTheme.text }}
                      data-testid="club-composer-input"
                    />
                    <div className="mt-1 text-[11px] opacity-70" style={{ color: activeTheme.text }}>
                      {content.length}/{THEMED_MAX_CHARS} · themed posts stay short
                    </div>
                  </div>
                ) : (
                  <textarea rows={3} maxLength={4000} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share a thought, a favorite passage, a question…" className="input-jj" data-testid="club-composer-input" />
                )}
                {themeOpen && (
                  <div className="mt-3 flex flex-wrap gap-2 items-center" data-testid="club-theme-picker">
                    <span className="text-xs text-[var(--jj-muted)] mr-1">Background:</span>
                    {BOOKCLUB_THEMES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setBackground(t.id)}
                        title={t.label}
                        aria-label={`Background ${t.label}`}
                        className={`h-8 w-8 rounded-full border-2 transition ${background === t.id ? "border-[var(--jj-blue)] scale-110" : "border-white hover:scale-105"} shadow-sm`}
                        style={{ background: t.swatch }}
                        data-testid={`club-theme-${t.id}`}
                      />
                    ))}
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setThemeOpen((v) => !v)}
                      className={`btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5 ${themeOpen ? "!border-[var(--jj-gold)] !bg-white" : ""}`}
                      data-testid="club-theme-btn">
                      <Palette className="h-3.5 w-3.5" strokeWidth={1.6} /> Background
                      {background !== "none" && (
                        <span
                          className="ml-1 h-2.5 w-2.5 rounded-full border border-white shadow"
                          style={{ background: activeTheme.swatch }}
                          aria-hidden
                        />
                      )}
                    </button>
                    <span className="text-[11px] text-[var(--jj-muted)]">{content.length}/4000</span>
                  </div>
                  <button disabled={busy || !content.trim()} className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-2" data-testid="club-composer-submit">
                    <Send className="h-4 w-4" /> {busy ? "Sending…" : "Post"}
                  </button>
                </div>
              </form>
            );
          })()
        ) : (
          <div className="mt-6 jj-card p-4 text-center text-sm text-[var(--jj-muted)]">
            {user ? "Join the club to post in the discussion." : (
              <>Please <Link to="/login" className="text-burgundy hover:underline">sign in</Link> and join to post.</>
            )}
          </div>
        )}
      </section>
      {sharePostId && (() => {
        const p = posts.find((x) => x.id === sharePostId);
        if (!p) return null;
        return (
          <ShareCardDialog
            open={true}
            onClose={() => setSharePostId(null)}
            content={p.content}
            background={p.background}
            authorName={p.author_name}
            kind="club"
          />
        );
      })()}
      <EditClubDialog
        open={editClubOpen}
        club={club}
        onClose={() => setEditClubOpen(false)}
        onSaved={(updated) => setClub((prev) => ({ ...prev, ...updated }))}
      />
    </div>
  );
}
